import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Layers, Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Trash2, Image, Link2,
} from "lucide-react";

function Stat({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-800", green: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`tabular text-lg font-black ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function CheckRow({ c }) {
  return (
    <div data-testid={`bl-check-${c.key}`} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
      <span className="text-slate-600">{c.label}</span>
      <span className="flex items-center gap-2">
        <span className="tabular font-bold text-slate-800">{c.found}</span>
        <span className="text-slate-300">/</span>
        <span className="tabular text-slate-400">{c.expected}</span>
        {c.ok
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid={`bl-check-ok-${c.key}`} />
          : <XCircle className="h-4 w-4 text-red-600" data-testid={`bl-check-fail-${c.key}`} />}
      </span>
    </div>
  );
}

export default function BirlesikImport() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dry, setDry] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);

  const pick = (f) => { setFile(f); setDry(null); setResult(null); };

  const runDry = async () => {
    if (!file) return toast.error("Birleşik katalog .xlsx dosyasını seçin");
    setBusy(true); setDry(null); setResult(null);
    try {
      const f = new FormData(); f.append("file", file);
      const { data } = await api.post("/products/import/birlesik-dry-run", f, { headers: { "Content-Type": "multipart/form-data" } });
      setDry(data);
      toast.success(data.can_commit ? "Analiz tamam — içe aktarmaya hazır" : "Analiz tamam — kontrol toplamı uyuşmuyor");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Analiz başarısız"); }
    finally { setBusy(false); }
  };

  const runCommit = async () => {
    if (confirmText.trim().toLocaleUpperCase("tr") !== "ONAYLA") return toast.error('Onay için kutuya "ONAYLA" yazın');
    setBusy(true);
    try {
      const f = new FormData(); f.append("file", file); f.append("confirm", "ONAYLA");
      const { data } = await api.post("/products/import/birlesik-commit", f, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); setDry(null); setConfirmText("");
      toast.success(`İçe aktarma tamam — ${data.products_created} kanonik ürün, ${data.aliases_created} alias`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İçe aktarma başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="birlesik-import">
      <div className="rounded-2xl border-2 border-brand-green/30 bg-gradient-to-br from-emerald-50/60 to-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-800">
          <Layers className="h-5 w-5 text-brand-green" /> Birleşik Katalog (2026) — Kanonik İçe Aktarma
          <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-xs font-bold text-brand-green">Önerilen</span>
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Tek dosya (<b>Görsel & Piyasa dahil</b>). <b>Tek fiziksel ürün = tek kart + tek SKU + ortak stok</b>;
          Market/Toptan aynı ürünün kanal fiyatı/kategorisidir. Önce <b>analiz</b> çalışır — hiçbir şey silinmez.
          Yalnız <b>Yönetici</b> onaylayınca TÜM mevcut katalog yedeklenip birleşik katalogdan yeniden kurulur.
        </p>

        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Birleşik Katalog Excel (.xlsx)</label>
          <input data-testid="bl-file" type="file" accept=".xlsx"
            onChange={(e) => pick(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white hover:file:bg-brand-green-600" />
          {file && <p className="mt-1 text-xs text-emerald-600">{file.name}</p>}
        </div>

        <Button data-testid="bl-dryrun-btn" onClick={runDry} disabled={busy || !file}
          className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">
          {busy && !dry ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Analiz Et (Dry-run)
        </Button>
      </div>

      {dry && (
        <div data-testid="bl-dry-report" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-slate-800">Kontrol Toplamı Raporu</h3>
            {dry.can_commit
              ? <span data-testid="bl-can-commit" className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> İçe aktarmaya hazır</span>
              : <span data-testid="bl-cannot-commit" className="flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700"><AlertTriangle className="h-4 w-4" /> Kontrol toplamı uyuşmuyor</span>}
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <Stat label="Yüklenecek ürün" value={dry.incoming_total} tone="green" />
            <Stat label="Silinecek ürün" value={dry.existing_to_delete?.products} tone="red" />
            <Stat label="Silinecek kategori" value={dry.existing_to_delete?.categories} tone="red" />
            <Stat label="Manuel KDV" value={dry.manual_kdv_count} tone="amber" />
            <Stat label="Taze görsel kilidi" value={dry.fresh_locks} />
            <Stat label="Piyasa benchmarkı" value={dry.benchmarks} />
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {(dry.checks || []).map((c) => <CheckRow key={c.key} c={c} />)}
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
            {Object.entries(dry.conflict_counts || {}).map(([k, v]) => (
              <span key={k} className={`rounded-md px-2 py-1 font-semibold ring-1 ring-inset ${v > 0 ? "bg-red-50 text-red-700 ring-red-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}>
                {k}: {v}
              </span>
            ))}
          </div>

          <div className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
            <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Kurallar</p>
            <ul className="ml-5 mt-1 list-disc space-y-0.5">
              <li className="flex items-start gap-1"><Image className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {dry.notes?.fresh_lock}</li>
              <li>{dry.notes?.empty_price}</li>
              <li className="flex items-start gap-1"><Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {dry.notes?.sku_immutable}</li>
              <li>{dry.notes?.stock_zero}</li>
              <li className="font-semibold text-red-700">{dry.notes?.wipe_replace}</li>
            </ul>
          </div>

          {isAdmin ? (
            <div className="mt-5 rounded-xl border-2 border-red-200 bg-red-50/50 p-4">
              <p className="flex items-center gap-1.5 text-sm font-bold text-red-700"><Trash2 className="h-4 w-4" /> Kalıcı içe aktarma (yıkıcı)</p>
              <p className="mt-1 text-xs text-slate-600">Onaylamak için kutuya <b>ONAYLA</b> yazın. Tüm mevcut ürün/kategoriler yedeklenip silinecek ve birleşik katalog kurulacaktır.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input data-testid="bl-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="ONAYLA" disabled={!dry.can_commit}
                  className="w-40 rounded-md border border-slate-300 px-3 py-2 text-sm uppercase tracking-widest disabled:opacity-50" />
                <Button data-testid="bl-commit-btn" onClick={runCommit} disabled={busy || !dry.can_commit || confirmText.trim().toLocaleUpperCase("tr") !== "ONAYLA"}
                  className="bg-red-600 text-white hover:bg-red-700">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} İçe Aktar (Kalıcı)
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-4 rounded-md bg-slate-100 p-3 text-xs text-slate-500">İçe aktarma yalnızca Yönetici tarafından onaylanabilir. Bu salt-okunur bir önizlemedir.</p>
          )}
        </div>
      )}

      {result && (
        <div data-testid="bl-result" className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold text-emerald-800"><CheckCircle2 className="h-5 w-5" /> Birleşik katalog içe aktarıldı</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            <Stat label="Kanonik ürün" value={result.products_created} tone="green" />
            <Stat label="Kategori" value={result.categories_created} tone="green" />
            <Stat label="Alias SKU" value={result.aliases_created} />
            <Stat label="Piyasa gözlemi" value={result.observations_created} />
            <Stat label="Toplam ürün (DB)" value={result.total_products} />
            <Stat label="Manuel KDV" value={result.manual_kdv_count} tone="amber" />
          </div>
          <p className="mt-3 text-xs text-slate-600">Yedek: <b>{result.backup?.products || 0}</b> ürün / <b>{result.backup?.categories || 0}</b> kategori
            (<span className="font-mono">products_import_backup_{result.backup?.ts}</span>). Gerekirse aşağıdaki "İçe Aktarma Geçmişi"nden geri yükleyebilirsiniz.</p>
        </div>
      )}
    </div>
  );
}
